/**
 * TenantExpandedRow
 * Expanded tenant details row for Dashboard policy table
 * Shows deployment status per tenant with popover details
 */

import React from 'react';
import {
  Text,
  Popover,
  PopoverTrigger,
  PopoverSurface,
} from '@fluentui/react-components';
import { FormattedDateTime, TimeColumnHeader } from '../../common/DateTimeWithUTCInfo';
import { DeploymentStatus } from './DeployStatusBadge';
import { StatusBadge, StatusType } from '../../StatusBadge';
import { AvailableStatus, PartiallyDeployedStatus } from '../../../common/constants';
import { TenantDeployment, TenantRuleStatus } from '../types';

/** Maps a per-tenant deployment status to the shared StatusBadge type so the
 *  expanded-row pills match View 3 (and View 4's own main row). */
function deploymentStatusToBadge(status: DeploymentStatus): StatusType {
  switch (status) {
    case 'Deployed': return AvailableStatus;
    case 'PartiallyDeployed': return PartiallyDeployedStatus;
    case 'InProgress': return 'In Progress';
    case 'DeploymentFailed': return 'Failed';
    default: return 'Not Available';
  }
}

interface TenantExpandedRowProps {
  tenantDeployments: TenantDeployment[];
  getTenantNameById: (tenantId: string) => string;
  getTenantPrefixById?: (tenantId: string) => string;
  isLastRow?: boolean;
}

export function TenantExpandedRow({ tenantDeployments, getTenantNameById, getTenantPrefixById, isLastRow = false }: TenantExpandedRowProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '48px minmax(250px,2.5fr) minmax(100px,0.8fr) minmax(180px,1.5fr) minmax(180px,1.5fr) minmax(180px,1.5fr)',
        padding: '12px 20px 16px 20px',
        borderBottom: isLastRow ? 'none' : '1px solid #f0f0f0',
        background: '#f8f9fa',
      }}
    >
      <div />
      <div style={{ gridColumn: '2 / -1' }}>
        {/* Tenant details table */}
        <div
          style={{
            border: '1px solid #e5e5e5',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(200px, 2fr) minmax(90px, 0.7fr) minmax(120px, 1fr) minmax(140px, 1.2fr) minmax(180px, 1.5fr)',
              padding: '12px 16px',
              background: '#fafafa',
              borderBottom: '1px solid #e5e5e5',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <div style={{ borderRight: '1px solid #e5e5e5', paddingRight: 12 }}>Tenant Name</div>
            <div style={{ borderRight: '1px solid #e5e5e5', paddingRight: 12, textAlign: 'center' }}>Prefix</div>
            <div style={{ borderRight: '1px solid #e5e5e5', paddingRight: 12, textAlign: 'center' }}>Status</div>
            <div style={{ borderRight: '1px solid #e5e5e5', paddingRight: 12, textAlign: 'center' }}>Rules</div>
            <div style={{ paddingLeft: 12, display: 'flex', justifyContent: 'center' }}>
              <TimeColumnHeader>Last Deployed</TimeColumnHeader>
            </div>
          </div>
          
          {/* Rows */}
          {tenantDeployments.map((tenantDeployment, idx) => (
            <TenantDetailRow
              key={tenantDeployment.tenantId}
              tenantDeployment={tenantDeployment}
              getTenantNameById={getTenantNameById}
              getTenantPrefixById={getTenantPrefixById}
              isLastRow={idx === tenantDeployments.length - 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface TenantDetailRowProps {
  tenantDeployment: TenantDeployment;
  getTenantNameById: (tenantId: string) => string;
  getTenantPrefixById?: (tenantId: string) => string;
  isLastRow?: boolean;
}

function TenantDetailRow({ tenantDeployment, getTenantNameById, getTenantPrefixById, isLastRow = false }: TenantDetailRowProps) {
  const deployedPercent = tenantDeployment.totalRulesCount > 0
    ? Math.round((tenantDeployment.deployedRulesCount / tenantDeployment.totalRulesCount) * 100)
    : 0;
  
  const deployedRules = tenantDeployment.ruleStatuses.filter(rs => rs.isDeployed);
  const notDeployedRules = tenantDeployment.ruleStatuses.filter(rs => !rs.isDeployed);
  const prefix = getTenantPrefixById?.(tenantDeployment.tenantId) ?? '';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(200px, 2fr) minmax(90px, 0.7fr) minmax(120px, 1fr) minmax(140px, 1.2fr) minmax(180px, 1.5fr)',
        padding: '12px 16px',
        fontSize: 13,
        borderBottom: isLastRow ? 'none' : '1px solid #f0f0f0',
        background: 'white',
      }}
    >
      <div style={{ borderRight: '1px solid #e5e5e5', paddingRight: 12 }}>
        {getTenantNameById(tenantDeployment.tenantId)}
      </div>
      <div style={{ borderRight: '1px solid #e5e5e5', paddingRight: 12, textAlign: 'center' }}>
        {prefix ? (
          <span style={{ fontWeight: 600, color: '#0078d4' }}>{prefix}</span>
        ) : (
          <span style={{ color: '#6b7280' }}>—</span>
        )}
      </div>
      <div style={{ borderRight: '1px solid #e5e5e5', paddingRight: 12, textAlign: 'center' }}>
        <StatusBadge status={deploymentStatusToBadge(tenantDeployment.status as DeploymentStatus)} />
      </div>
      <div style={{ borderRight: '1px solid #e5e5e5', paddingRight: 12, textAlign: 'center' }}>
        <RulesPopover
          deployedCount={tenantDeployment.deployedRulesCount}
          totalCount={tenantDeployment.totalRulesCount}
          deployedPercent={deployedPercent}
          deployedRules={deployedRules}
          notDeployedRules={notDeployedRules}
        />
      </div>
      <div style={{ textAlign: 'center' }}>
        {tenantDeployment.lastDeployed ? (
          <FormattedDateTime dateTime={tenantDeployment.lastDeployed} />
        ) : (
          <span style={{ color: '#6b7280' }}>—</span>
        )}
      </div>
    </div>
  );
}

interface RulesPopoverProps {
  deployedCount: number;
  totalCount: number;
  deployedPercent: number;
  deployedRules: TenantRuleStatus[];
  notDeployedRules: TenantRuleStatus[];
}

function RulesPopover({
  deployedCount,
  totalCount,
  deployedPercent,
  deployedRules,
  notDeployedRules,
}: RulesPopoverProps) {
  return (
    <Popover withArrow>
      <PopoverTrigger disableButtonEnhancement>
        <span 
          style={{ 
            fontSize: 12, 
            cursor: 'pointer',
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
            color: '#0078d4',
          }}
        >
          {deployedCount}/{totalCount} rules deployed ({deployedPercent}%)
        </span>
      </PopoverTrigger>
      <PopoverSurface style={{ padding: 16, maxWidth: 320 }}>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontWeight: 600, fontSize: 14, display: 'block', marginBottom: 8 }}>
            Rule Deployment Status
          </Text>
          <Text style={{ fontSize: 12, color: '#6b7280' }}>
            {deployedCount} of {totalCount} rules deployed
          </Text>
        </div>
        
        {deployedRules.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text style={{ fontWeight: 600, fontSize: 12, color: '#107c10', display: 'block', marginBottom: 4 }}>
              ✓ Deployed ({deployedRules.length})
            </Text>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#444' }}>
              {deployedRules.map(r => (
                <li key={r.ruleId} style={{ marginBottom: 2 }}>{r.ruleName}</li>
              ))}
            </ul>
          </div>
        )}
        
        {notDeployedRules.length > 0 && (
          <div>
            <Text style={{ fontWeight: 600, fontSize: 12, color: '#d13438', display: 'block', marginBottom: 4 }}>
              ✗ Not Deployed ({notDeployedRules.length})
            </Text>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#444' }}>
              {notDeployedRules.map(r => (
                <li key={r.ruleId} style={{ marginBottom: 2 }}>{r.ruleName}</li>
              ))}
            </ul>
          </div>
        )}
      </PopoverSurface>
    </Popover>
  );
}
